import React from 'react';
import { Box, IconButton, OutlinedInput, Typography, Chip, Paper, Divider } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Button, FormControl, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { FilterGroup, FilterCondition } from 'types/userConfig/userConfig';
import useLocalization from 'hooks/useLocalization';
import { DateTimePicker } from '@mui/x-date-pickers';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { mapFieldToApiField } from 'utils/field-mapping';
import { ChatUserModel } from 'types/chat-users/chat-user';
import dayjs from 'dayjs';
import { createDynamicValue, isDynamicValue } from 'utils/resolve-dynamic-values';
import { store } from 'store';
import { enqueueSnackbar } from 'notistack';

export type ViewFilterOptions = {
  key: keyof ChatUserModel | string; // מאפשר גם שדות שלא קיימים ב-ChatUserModel (כמו IsOpenUpdateTimeFrom)
  label: string | null;
  type: 'input' | 'select' | 'dateTime' | 'checkbox';
  options?: Array<{ value: any; label: string | null }>;
  multiple?: boolean;
  operators?: Array<'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains' | 'startswith' | 'endswith' | 'in' | 'notin' | 'isnull' | 'isnotnull'>;
  category?: 'contact' | 'lastSession' | 'lastMessageObject' | 'lastIncomingMessage' | 'lastOutgoingMessage'; // קטגוריה
  categoryLabel?: string; // שם הקטגוריה בעברית (למשל "איש קשר", "שיחה", "שיחה אחרונה")
};

interface CreateCustomViewFilterGroupsProps {
  options: Array<ViewFilterOptions>;
  filterGroups: FilterGroup[];
  filtersLogic: 'or' | 'and';
  onFilterGroupsChange: (groups: FilterGroup[]) => void;
  onFiltersLogicChange: (logic: 'or' | 'and') => void;
}

const operatorLabels: Record<string, string> = {
  eq: 'שווה ל',
  ne: 'לא שווה ל',
  gt: 'גדול מ',
  gte: 'גדול או שווה ל',
  lt: 'קטן מ',
  lte: 'קטן או שווה ל',
  contains: 'מכיל',
  startswith: 'מתחיל ב',
  endswith: 'מסתיים ב',
  in: 'נמצא ב',
  notin: 'לא נמצא ב',
  isnull: 'ריק',
  isnotnull: 'לא ריק',
};

const CreateCustomViewFilterGroups: React.FC<CreateCustomViewFilterGroupsProps> = ({
  options,
  filterGroups,
  filtersLogic,
  onFilterGroupsChange,
  onFiltersLogicChange,
}) => {
  const { currentLocale } = useLocalization();
  const localization = React.useMemo(
    () => currentLocale.settingsPopup.navigation.filters.create,
    [currentLocale],
  );

  // State לניהול קטגוריות פתוחות/סגורות (אקורדיון)
  const [expandedCategories, setExpandedCategories] = React.useState<Set<string>>(new Set());
  
  // State לניהול איזה Select פתוח (למניעת סגירה אוטומטית)
  const [openSelectId, setOpenSelectId] = React.useState<string | number | null>(null);
  
  // State לזיהוי אם לחיצה אחרונה הייתה על קטגוריה (למניעת סגירה)
  const [lastClickWasCategory, setLastClickWasCategory] = React.useState(false);

  // פונקציה לפתיחה/סגירה של קטגוריה
  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const addNewGroup = () => {
    const newGroup: FilterGroup = {
      id: Date.now(),
      logic: 'and',
      conditions: [],
    };
    onFilterGroupsChange([...filterGroups, newGroup]);
  };

  const deleteGroup = (groupId: string | number) => {
    onFilterGroupsChange(filterGroups.filter((group) => group.id !== groupId));
  };

  const addConditionToGroup = (groupId: string | number) => {
    const newCondition: FilterCondition = {
      id: Date.now(),
      field: '',
      operator: 'eq',
    };
    onFilterGroupsChange(
      filterGroups.map((group) =>
        group.id === groupId
          ? { ...group, conditions: [...group.conditions, newCondition] }
          : group,
      ),
    );
  };

  const deleteCondition = (groupId: string | number, conditionId: string | number) => {
    onFilterGroupsChange(
      filterGroups.map((group) =>
        group.id === groupId
          ? { ...group, conditions: group.conditions.filter((cond) => cond.id !== conditionId) }
          : group,
      ),
    );
  };

  const updateCondition = (
    groupId: string | number,
    conditionId: string | number,
    updates: Partial<FilterCondition>,
  ) => {
    console.log('updateCondition called:', { groupId, conditionId, updates, currentFilterGroups: filterGroups });
    const updatedGroups = filterGroups.map((group) =>
      group.id === groupId
        ? {
            ...group,
            conditions: group.conditions.map((cond) =>
              cond.id === conditionId ? { ...cond, ...updates } : cond,
            ),
          }
        : group,
    );
    console.log('Updated filter groups:', updatedGroups);
    onFilterGroupsChange(updatedGroups);
  };

  const handleFieldSelection = (groupId: string | number, conditionId: string | number, newField: string) => {
    const group = filterGroups.find((g) => g.id === groupId);
    if (!group) return;
    
    const newFieldOption = getFieldOption(newField);
    
    // שדות שלא ניתן להוסיף יותר מתנאי אחד שלהם באותה קבוצה עם לוגיקה "וגם"
    const singleValueFields = ['lastSession.manager.id', 'label', 'NotificationContactUser', 'LableName'];
    
    // בדוק אם יש כבר תנאי אחר בקבוצה עם אותו שדה
    // אסור רק אם הלוגיקה של הקבוצה היא "וגם" (AND)
    if (singleValueFields.includes(newField)) {
      const existingCondition = group.conditions.find(
        (cond) => cond.id !== conditionId && cond.field === newField
      );
      if (existingCondition && (group.logic === 'and' || !group.logic)) {
        // יש כבר תנאי עם אותו שדה והלוגיקה היא "וגם" - לא ניתן לבחור אותו שוב
        const fieldLabel = newFieldOption?.label || newField;
        enqueueSnackbar(
          `לא ניתן לבחור "${fieldLabel}" פעמיים כאשר הלוגיקה היא "וגם". שיחה לא יכולה להיות משויכת למספר משתמשים או עם מספר תגיות בו-זמנית.`,
          { variant: 'warning' }
        );
        return;
      }
    }
    
    // עדכן את השדה
    updateCondition(groupId, conditionId, {
      field: newField,
      operator: 'eq', // איפוס operator
      value: undefined, // איפוס value
    });
  };

  const updateGroupLogic = (groupId: string | number, logic: 'or' | 'and') => {
    const group = filterGroups.find((g) => g.id === groupId);
    if (!group) return;
    
    // אם משנים לוגיקה ל"וגם", בדוק אם יש שני תנאים עם אותו שדה
    if (logic === 'and') {
      const singleValueFields = ['lastSession.manager.id', 'label', 'NotificationContactUser', 'LableName'];
      const fieldCounts: Record<string, number> = {};
      const duplicateFields: string[] = [];
      
      // ספור כמה פעמים כל שדה מופיע
      group.conditions.forEach((cond) => {
        if (cond.field && singleValueFields.includes(cond.field)) {
          fieldCounts[cond.field] = (fieldCounts[cond.field] || 0) + 1;
          if (fieldCounts[cond.field] === 2) {
            // מצא את התווית של השדה
            const fieldOption = getFieldOption(cond.field);
            if (fieldOption?.label && !duplicateFields.includes(fieldOption.label)) {
              duplicateFields.push(fieldOption.label);
            }
          }
        }
      });
      
      // אם יש שדה שמופיע יותר מפעם אחת, לא ניתן לשנות ל"וגם"
      const hasDuplicateField = Object.values(fieldCounts).some((count) => count > 1);
      if (hasDuplicateField) {
        // יש שני תנאים עם אותו שדה - לא ניתן לשנות ל"וגם"
        const fieldNames = duplicateFields.join(', ');
        enqueueSnackbar(
          `לא ניתן לשנות לוגיקה ל"וגם" כאשר יש מספר תנאים עם אותו שדה (${fieldNames}). שיחה לא יכולה להיות משויכת למספר משתמשים או עם מספר תגיות בו-זמנית.`,
          { variant: 'warning' }
        );
        return;
      }
    }
    
    onFilterGroupsChange(
      filterGroups.map((group) => (group.id === groupId ? { ...group, logic } : group)),
    );
  };

  // מחזיר רשימת קטגוריות ייחודיות בסדר שבו הן מופיעות ב-options
  const getCategories = (): Array<{ value: string; label: string }> => {
    const categoriesMap = new Map<string, string>();
    const categoryOrder: string[] = [];
    
    options.forEach((option) => {
      if (option.category && option.categoryLabel) {
        if (!categoriesMap.has(option.category)) {
          categoriesMap.set(option.category, option.categoryLabel);
          categoryOrder.push(option.category);
        }
      }
    });
    
    return categoryOrder.map((category) => ({
      value: category,
      label: categoriesMap.get(category) || category,
    }));
  };

  // מחזיר את כל השדות של קטגוריה מסוימת
  const getFieldsByCategory = (category: string): Array<ViewFilterOptions> => {
    return options.filter((option) => option.category === category);
  };

  // מחזיר את הקטגוריה של שדה מסוים
  const getCategoryByField = (field: string): string | undefined => {
    const option = getFieldOption(field);
    return option?.category;
  };

  const getOperatorsForField = (field: string): Array<'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains' | 'startswith' | 'endswith' | 'in' | 'notin' | 'isnull' | 'isnotnull'> => {
    // מצא את האופציה לפי ה-key המקורי (לא ה-API field)
    const option = options.find((opt) => String(opt.key) === field);
    if (option?.operators) {
      return option.operators;
    }
    // ברירת מחדל לפי סוג השדה
    if (option?.type === 'select' || option?.type === 'checkbox') {
      return ['eq', 'ne', 'in', 'notin', 'isnull', 'isnotnull'];
    }
    if (option?.type === 'dateTime') {
      return ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'isnull', 'isnotnull'];
    }
    if (option?.type === 'input') {
      return ['eq', 'ne', 'contains', 'startswith', 'endswith', 'isnull', 'isnotnull'];
    }
    return ['eq', 'ne', 'contains', 'isnull', 'isnotnull'];
  };

  const needsValue = (operator: string): boolean => {
    return !['isnull', 'isnotnull'].includes(operator);
  };

  const getFieldOption = (field: string): ViewFilterOptions | undefined => {
    // field הוא כעת ה-key המקורי, לא ה-API field
    return options.find((opt) => String(opt.key) === field);
  };

  return (
    <Box>
      {/* Logic בין קבוצות */}
      {filterGroups.length > 1 && (
        <Box sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>לוגיקה בין קבוצות</InputLabel>
            <Select
              value={filtersLogic}
              label="לוגיקה בין קבוצות"
              onChange={(e) => onFiltersLogicChange(e.target.value as 'or' | 'and')}
            >
              <MenuItem value="and">וגם (AND)</MenuItem>
              <MenuItem value="or">או (OR)</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {filterGroups.map((group, groupIndex) => {
        const categories = getCategories();
        return (
          <Paper
            key={group.id}
            elevation={2}
            sx={{
              p: 2,
              mb: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2" fontWeight="bold">
                קבוצה {groupIndex + 1}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {/* Logic בתוך קבוצה */}
                {group.conditions.length > 1 && (
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <Select
                      value={group.logic || 'and'}
                      onChange={(e) => updateGroupLogic(group.id, e.target.value as 'or' | 'and')}
                      sx={{ fontSize: '0.875rem' }}
                    >
                      <MenuItem value="and">וגם</MenuItem>
                      <MenuItem value="or">או</MenuItem>
                    </Select>
                  </FormControl>
                )}
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => deleteGroup(group.id)}
                  disabled={filterGroups.length === 1 && group.conditions.length === 0}
                >
                  <DeleteOutlinedIcon />
                </IconButton>
              </Box>
            </Box>

            <Grid container spacing={1.5}>
              {group.conditions.map((condition) => {
                const fieldOption = getFieldOption(condition.field);
                const availableOperators = getOperatorsForField(condition.field);

                return (
                  <Grid size={12} key={condition.id}>
                    <Grid container spacing={1} alignItems="center">
                      {/* Field */}
                      <Grid size={filterGroups.length > 1 ? 3 : 4}>
                        <FormControl fullWidth size="small">
                          <InputLabel>שדה</InputLabel>
                          <Select
                            value={condition.field || ''}
                            label="שדה"
                            open={openSelectId === condition.id}
                            onOpen={() => setOpenSelectId(condition.id)}
                            onClose={() => {
                              // אם הלחיצה האחרונה הייתה על קטגוריה, אל תסגור
                              if (lastClickWasCategory) {
                                setLastClickWasCategory(false);
                                // אל תסגור את ה-dropdown - נשאיר אותו פתוח
                                return;
                              }
                              setOpenSelectId(null);
                              setLastClickWasCategory(false);
                            }}
                            renderValue={(selected) => {
                              if (!selected || !condition.field) return '';
                              const fieldOption = getFieldOption(condition.field);
                              if (!fieldOption) return condition.field;
                              const categoryLabel = fieldOption.categoryLabel || '';
                              return `${fieldOption.label || condition.field}${categoryLabel ? ` (${categoryLabel})` : ''}`;
                            }}
                            onChange={(e) => {
                              const selectedValue = e.target.value;
                              // בדוק אם זה קטגוריה או שדה
                              const categoryOption = categories.find((cat) => cat.value === selectedValue);
                              if (categoryOption) {
                                // זה קטגוריה - פתח/סגור אותה
                                toggleCategory(selectedValue);
                                // סמן שהלחיצה האחרונה הייתה על קטגוריה
                                setLastClickWasCategory(true);
                                // אל תסגור את ה-dropdown ואל תעדכן את השדה
                                // נשאיר את ה-dropdown פתוח
                                return;
                              }
                              // זה שדה - עדכן את השדה וסגור את ה-dropdown
                              handleFieldSelection(group.id, condition.id, selectedValue);
                              setOpenSelectId(null);
                            }}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 400,
                                },
                              },
                              onClose: (event, reason) => {
                                // אם הלחיצה האחרונה הייתה על קטגוריה, אל תסגור
                                if (lastClickWasCategory) {
                                  setLastClickWasCategory(false);
                                  // אל תסגור את ה-dropdown - נשאיר אותו פתוח
                                  return;
                                }
                                // סגור רק אם זה backdrop או escape
                                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                                  setOpenSelectId(null);
                                  setLastClickWasCategory(false);
                                }
                              },
                            }}
                          >
                            {categories.map((category) => {
                              const isExpanded = expandedCategories.has(category.value);
                              const categoryFields = getFieldsByCategory(category.value);
                              
                              return (
                                <React.Fragment key={category.value}>
                                  {/* כותרת קטגוריה */}
                                  <MenuItem
                                    value={category.value}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      toggleCategory(category.value);
                                      // אל תסגור את ה-dropdown - נשאיר אותו פתוח
                                    }}
                                    onMouseDown={(e) => {
                                      // מניעת סגירה אוטומטית של ה-dropdown
                                      e.preventDefault();
                                    }}
                                    sx={{
                                      fontWeight: 'bold',
                                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                      '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                      },
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Box>{category.label}</Box>
                                    <Box>
                                      {isExpanded ? (
                                        <ChevronRightIcon fontSize="small" />
                                      ) : (
                                        <ExpandMoreIcon fontSize="small" />
                                      )}
                                    </Box>
                                  </MenuItem>
                                  {/* שדות תחת הקטגוריה */}
                                  {isExpanded &&
                                    categoryFields.map((fieldOption) => {
                                      // בדוק אם השדה כבר קיים בקבוצה עם לוגיקה "וגם"
                                      const singleValueFields = ['lastSession.manager.id', 'label', 'NotificationContactUser', 'LableName'];
                                      const isDisabled =
                                        singleValueFields.includes(fieldOption.key as string) &&
                                        (group.logic === 'and' || !group.logic) &&
                                        group.conditions.some(
                                          (cond) => cond.id !== condition.id && cond.field === fieldOption.key
                                        );

                                      return (
                                        <MenuItem
                                          key={fieldOption.key}
                                          value={fieldOption.key}
                                          disabled={isDisabled}
                                          onClick={() => {
                                            handleFieldSelection(group.id, condition.id, fieldOption.key as string);
                                            setOpenSelectId(null); // סגור את ה-dropdown
                                          }}
                                          sx={{
                                            pl: 6, // הזחה פנימה
                                          }}
                                        >
                                          {fieldOption.label || fieldOption.key}
                                        </MenuItem>
                                      );
                                    })}
                                </React.Fragment>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Operator */}
                      <Grid size={filterGroups.length > 1 ? 2 : 3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>אופרטור</InputLabel>
                          <Select
                            value={condition.operator}
                            label="אופרטור"
                            onChange={(e) => {
                              const newOperator = e.target.value as FilterCondition['operator'];
                              let newValue = condition.value;
                              
                              // אם משנים operator, צריך להתאים את הערך
                              if (needsValue(newOperator)) {
                                const isNewMultipleOperator = newOperator === 'in' || newOperator === 'notin';
                                const isCurrentMultipleOperator = condition.operator === 'in' || condition.operator === 'notin';
                                
                                if (isNewMultipleOperator && !isCurrentMultipleOperator) {
                                  // מעבר מ-eq/ne ל-in/notin - המר string ל-array
                                  newValue = condition.value ? [condition.value] : [];
                                } else if (!isNewMultipleOperator && isCurrentMultipleOperator) {
                                  // מעבר מ-in/notin ל-eq/ne - קח את האלמנט הראשון
                                  newValue = Array.isArray(condition.value) && condition.value.length > 0 
                                    ? condition.value[0] 
                                    : undefined;
                                }
                              } else {
                                // עבור isnull/isnotnull, אין צורך בערך
                                newValue = undefined;
                              }
                              
                              updateCondition(group.id, condition.id, {
                                operator: newOperator,
                                value: newValue,
                              });
                            }}
                          >
                            {availableOperators.map((op) => (
                              <MenuItem key={op} value={op}>
                                {operatorLabels[op] || op}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Value */}
                      {needsValue(condition.operator) && condition.field && (
                        <Grid size={filterGroups.length > 1 ? 5 : 4}>
                          {fieldOption?.type === 'select' && (() => {
                            // קבע אם צריך multiple select לפי ה-operator
                            // עבור 'in' ו-'notin' - multiple, אחרת - single
                            const isMultipleOperator = condition.operator === 'in' || condition.operator === 'notin';
                            const shouldBeMultiple = fieldOption.multiple && isMultipleOperator;
                            
                            // קבע את הערך הנוכחי - אם זה array ו-operator הוא eq/ne, קח את האלמנט הראשון
                            let currentValue: any = condition.value;
                            if (Array.isArray(currentValue) && !isMultipleOperator) {
                              currentValue = currentValue[0] || '';
                            } else if (!Array.isArray(currentValue) && isMultipleOperator) {
                              currentValue = currentValue ? [currentValue] : [];
                            } else if (currentValue === null || currentValue === undefined) {
                              currentValue = shouldBeMultiple ? [] : '';
                            }
                            // שמור את הערך המקורי (יכול להיות ערך דינמי)
                            const originalValue = currentValue;

                            // בדיקה אם זה שדה manager - להוסיף אופציה "משתמש נוכחי"
                            // condition.field הוא ה-key המקורי (כמו NotificationContactUser)
                            // אבל צריך לבדוק גם אם זה ממופה ל-managerId ב-API
                            const fieldOptionForCheck = getFieldOption(condition.field);
                            const apiField = fieldOptionForCheck ? mapFieldToApiField(fieldOptionForCheck.key) : condition.field;
                            const isManagerField = condition.field === 'NotificationContactUser' || 
                                                   condition.field === 'lastSession.manager.id' || 
                                                   apiField === 'managerId';
                            const currentUser = store.getState().user;
                            const currentUserDynamicValue = createDynamicValue('current_user');
                            
                            console.log('Select render - condition.field:', condition.field, 'apiField:', apiField, 'isManagerField:', isManagerField, 'currentUser:', currentUser, 'currentUserDynamicValue:', currentUserDynamicValue, 'fieldOption:', fieldOption);
                            
                            // בדיקה אם הערך הנוכחי הוא ערך דינמי של משתמש נוכחי
                            const isCurrentUserSelected = originalValue === currentUserDynamicValue;

                            return (
                              <FormControl fullWidth size="small">
                                <InputLabel>ערך</InputLabel>
                                <Select
                                  value={originalValue ?? ''}
                                  label="ערך"
                                  displayEmpty
                                  renderValue={(selected) => {
                                    console.log('renderValue called with selected:', selected, 'type:', typeof selected, 'currentUserDynamicValue:', currentUserDynamicValue);
                                    // עבור multiple select, selected הוא array
                                    if (shouldBeMultiple && Array.isArray(selected)) {
                                      return selected.length > 0 ? selected.join(', ') : '';
                                    }
                                    // עבור single select
                                    if (selected === currentUserDynamicValue || String(selected) === String(currentUserDynamicValue)) {
                                      return `משתמש נוכחי (${currentUser?.FullName || currentUser?.Udid || ''})`;
                                    }
                                    // מצא את התווית מהאופציות הרגילות
                                    const option = fieldOption.options?.find(opt => opt.value === selected || String(opt.value) === String(selected));
                                    return option ? option.label : selected || '';
                                  }}
                                  onChange={(e) => {
                                    console.log('Select onChange EVENT TRIGGERED!', e);
                                    const value = e.target.value;
                                    console.log('Select onChange - value:', value, 'type:', typeof value, 'isManagerField:', isManagerField, 'currentUserDynamicValue:', currentUserDynamicValue, 'shouldBeMultiple:', shouldBeMultiple);
                                    let finalValue: any;
                                    
                                    if (shouldBeMultiple) {
                                      // עבור multiple select
                                      finalValue = typeof value === 'string' ? value.split(',') : value;
                                    } else {
                                      // עבור single select - תמיד string
                                      finalValue = value || null;
                                    }
                                    
                                    console.log('Calling updateCondition with:', { groupId: group.id, conditionId: condition.id, value: finalValue });
                                    updateCondition(group.id, condition.id, {
                                      value: finalValue,
                                    });
                                  }}
                                  multiple={shouldBeMultiple}
                                  input={
                                    shouldBeMultiple ? <OutlinedInput label="ערך" /> : undefined
                                  }
                                >
                                  {/* אופציות דינמיות עבור manager */}
                                  {isManagerField && !shouldBeMultiple && currentUser?.Udid && (
                                    <React.Fragment key="current-user-option">
                                      <MenuItem 
                                        key={currentUserDynamicValue}
                                        value={currentUserDynamicValue}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          console.log('MenuItem onClick - currentUserDynamicValue:', currentUserDynamicValue);
                                          // קריאה ישירה ל-onChange
                                          const syntheticEvent = {
                                            target: { value: currentUserDynamicValue },
                                          } as React.ChangeEvent<{ value: unknown }>;
                                          // ננסה לקרוא ל-onChange ישירות
                                          console.log('Calling onChange directly with:', currentUserDynamicValue);
                                          updateCondition(group.id, condition.id, {
                                            value: currentUserDynamicValue,
                                          });
                                        }}
                                      >
                                        משתמש נוכחי ({currentUser.FullName || currentUser.Udid})
                                      </MenuItem>
                                      <Divider key="divider-after-current-user" />
                                    </React.Fragment>
                                  )}
                                  {fieldOption.options?.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                      {option.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            );
                          })()}
                          {fieldOption?.type === 'input' && (
                            <TextField
                              fullWidth
                              size="small"
                              label="ערך"
                              value={condition.value || ''}
                              onChange={(e) =>
                                updateCondition(group.id, condition.id, { value: e.target.value })
                              }
                            />
                          )}
                          {fieldOption?.type === 'dateTime' && (
                            <Grid container spacing={1}>
                              <Grid size={6}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>ערך דינמי</InputLabel>
                                  <Select
                                    value={isDynamicValue(condition.value) ? condition.value : ''}
                                    label="ערך דינמי"
                                    onChange={(e) => {
                                      if (e.target.value) {
                                        updateCondition(group.id, condition.id, {
                                          value: e.target.value,
                                        });
                                      } else {
                                        // אם בוחרים "בחר תאריך ידני", ננקה את הערך הדינמי
                                        updateCondition(group.id, condition.id, {
                                          value: null,
                                        });
                                      }
                                    }}
                                  >
                                    <MenuItem value="">בחר תאריך ידני</MenuItem>
                                    <Divider />
                                    <MenuItem value={createDynamicValue('today')}>היום</MenuItem>
                                    <MenuItem value={createDynamicValue('yesterday')}>אתמול</MenuItem>
                                    <MenuItem value={createDynamicValue('this_week')}>השבוע הנוכחי</MenuItem>
                                    <MenuItem value={createDynamicValue('last_week')}>השבוע שעבר</MenuItem>
                                    <MenuItem value={createDynamicValue('this_month')}>החודש הנוכחי</MenuItem>
                                    <MenuItem value={createDynamicValue('last_month')}>החודש שעבר</MenuItem>
                                    <Divider />
                                    <MenuItem value={createDynamicValue('last_7_days')}>7 הימים האחרונים</MenuItem>
                                    <MenuItem value={createDynamicValue('last_30_days')}>30 הימים האחרונים</MenuItem>
                                    <MenuItem value={createDynamicValue('last_90_days')}>90 הימים האחרונים</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid size={6}>
                                {!isDynamicValue(condition.value) && (
                                  <DateTimePicker
                                    label="ערך"
                                    value={condition.value ? dayjs(condition.value) : null}
                                    onChange={(newValue) =>
                                      updateCondition(group.id, condition.id, {
                                        value: newValue?.toISOString() || null,
                                      })
                                    }
                                    ampm={false}
                                    sx={{ width: '100%' }}
                                    slotProps={{
                                      textField: {
                                        size: 'small',
                                      },
                                    }}
                                  />
                                )}
                                {isDynamicValue(condition.value) && (
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="ערך נבחר"
                                    value={
                                      condition.value === createDynamicValue('today') ? 'היום' :
                                      condition.value === createDynamicValue('yesterday') ? 'אתמול' :
                                      condition.value === createDynamicValue('this_week') ? 'השבוע הנוכחי' :
                                      condition.value === createDynamicValue('last_week') ? 'השבוע שעבר' :
                                      condition.value === createDynamicValue('this_month') ? 'החודש הנוכחי' :
                                      condition.value === createDynamicValue('last_month') ? 'החודש שעבר' :
                                      condition.value === createDynamicValue('last_7_days') ? '7 הימים האחרונים' :
                                      condition.value === createDynamicValue('last_30_days') ? '30 הימים האחרונים' :
                                      condition.value === createDynamicValue('last_90_days') ? '90 הימים האחרונים' :
                                      'ערך דינמי'
                                    }
                                    disabled
                                  />
                                )}
                              </Grid>
                            </Grid>
                          )}
                          {!fieldOption && (
                            <TextField
                              fullWidth
                              size="small"
                              label="ערך"
                              value={condition.value || ''}
                              onChange={(e) =>
                                updateCondition(group.id, condition.id, { value: e.target.value })
                              }
                            />
                          )}
                        </Grid>
                      )}

                      {/* Delete Condition */}
                      <Grid size="auto">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => deleteCondition(group.id, condition.id)}
                        >
                          <DeleteOutlinedIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Grid>
                );
              })}

              {/* Add Condition Button */}
              <Grid size={12}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => addConditionToGroup(group.id)}
                >
                  הוסף תנאי
                </Button>
              </Grid>
            </Grid>
          </Paper>
        );
      })}

      {/* Add Group Button */}
      <Button variant="contained" startIcon={<AddIcon />} onClick={addNewGroup}>
        הוסף קבוצת פילטרים
      </Button>
    </Box>
  );
};

export default CreateCustomViewFilterGroups;

